import React, { useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { useFlag, useFlagsStatus } from "@unleash/proxy-client-react";
import { useTranslation } from "react-i18next";
import { Redirect, useLocation, useParams } from "react-router-dom";

import Button from "../../components/atoms/Button";
import { Container } from "../../components/atoms/Container";
import StyledLoading from "../../components/atoms/Loading/StyledLoading";
import { Hero, HeroTitle } from "../../components/molecules/Hero";
import PageMeta from "../../components/molecules/PageMeta";
import SpecialPackProductCard from "../../components/molecules/SpecialPackProductCard";
import SpecialPackCollectionGallery from "../../components/organisms/SpecialPackCollectionGallery";
import SpecialPackProductAddTray from "../../components/organisms/SpecialPackProductAddTray";
import {
  GET_ALL_NON_WINE_SPECIAL_PACK_PRODUCTS,
  GET_SPECIAL_PACK_COLLECTION,
  GET_SPECIAL_PACK_COLLECTIONS,
} from "../../graphql/resolvers/specialPack";
import {
  FETCH_POLICY_CACHE_FIRST,
  FETCH_POLICY_NETWORK_ONLY,
  I18N_NAMESPACES,
  PRODUCT_COLLECTIONS_PAGINATION_LIMIT,
  PRODUCT_COLLECTION_PRODUCT_LIMIT,
  UNLEASH_BEER_PACKS,
  UNLEASH_NEW_PRODUCT_CATEGORIES,
  UNLEASH_NON_WINE_SPECIAL_PACKS,
} from "../../helpers/constants";
import { logError } from "../../helpers/logging";
import {
  AllNonWineSpecialPackProducts,
  AllNonWineSpecialPackProductsVariables,
} from "../../types/__generated__/AllNonWineSpecialPackProducts";
import {
  SpecialPackCollections as SpecialPackCollectionsData,
  SpecialPackCollectionsVariables,
} from "../../types/__generated__/SpecialPackCollections";
import {
  SpecialPackCollection,
  SpecialPackCollectionVariables,
} from "../../types/__generated__/SpecialPackCollection";
import { SpecialPackProduct } from "../../types/__generated__/SpecialPackProduct";
import URL_PATTERNS from "../../urls";
import { PacksSection, PageDescription, ProductGrid } from "./style";

const SpecialPackCollections = () => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.SPECIAL_PACKS]);
  const { flagsReady, flagsError } = useFlagsStatus();
  const isNewProductCategoriesEnabled = useFlag(UNLEASH_NEW_PRODUCT_CATEGORIES);
  const isNonWineSpecialPacksEnabled = useFlag(UNLEASH_NON_WINE_SPECIAL_PACKS);
  const isEnabled = flagsReady && isNewProductCategoriesEnabled && isNonWineSpecialPacksEnabled;
  const isBeerPacksEnabled = useFlag(UNLEASH_BEER_PACKS);
  const pageTitle = t("common:specialPacks");
  const pageDescription = t(
    isBeerPacksEnabled ? "specialPacks:beerPacksDescription" : "specialPacks:collectionsDescription"
  );
  const { pathname } = useLocation();
  const { slug } = useParams<{ slug?: string }>();
  const isAll = pathname.replace(/\/$/, "") === URL_PATTERNS.ALL_SPECIAL_PACKS.replace(/\/$/, "");
  const locationKey = `${pathname}:${slug ?? ""}`;
  const locationKeyRef = useRef(locationKey);
  locationKeyRef.current = locationKey;
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);
  const [paginationLocation, setPaginationLocation] = useState(locationKey);
  // Pack option whose ADD was clicked; opens the wine packs' options tray for its edition
  const [addingProduct, setAddingProduct] = useState<SpecialPackProduct | null>(null);

  if (paginationLocation !== locationKey) {
    setPaginationLocation(locationKey);
    setIsFetchingMore(false);
    setHasFetchError(false);
  }

  const allPacksQuery = useQuery<AllNonWineSpecialPackProducts, AllNonWineSpecialPackProductsVariables>(
    GET_ALL_NON_WINE_SPECIAL_PACK_PRODUCTS,
    {
      variables: { limit: PRODUCT_COLLECTION_PRODUCT_LIMIT, offset: 0, availableOnly: true },
      fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
      nextFetchPolicy: FETCH_POLICY_CACHE_FIRST,
      skip: !isEnabled || !isAll,
    }
  );
  const collectionsQuery = useQuery<SpecialPackCollectionsData, SpecialPackCollectionsVariables>(
    GET_SPECIAL_PACK_COLLECTIONS,
    {
      variables: {
        limit: PRODUCT_COLLECTIONS_PAGINATION_LIMIT,
        offset: 0,
        productLimit: PRODUCT_COLLECTION_PRODUCT_LIMIT,
      },
      fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
      nextFetchPolicy: FETCH_POLICY_CACHE_FIRST,
      skip: !isEnabled || isAll || !!slug,
    }
  );
  const collectionQuery = useQuery<SpecialPackCollection, SpecialPackCollectionVariables>(
    GET_SPECIAL_PACK_COLLECTION,
    {
      variables: { slug: slug || "", limit: PRODUCT_COLLECTION_PRODUCT_LIMIT, offset: 0 },
      fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
      nextFetchPolicy: FETCH_POLICY_CACHE_FIRST,
      skip: !isEnabled || !slug,
    }
  );

  const collections = collectionsQuery.data?.collections || [];
  const collection = slug ? collectionQuery.data?.collectionBySlug : undefined;
  const products = isAll
    ? allPacksQuery.data?.allNonWineSpecialPackProducts || []
    : collection?.products || [];
  const listQuery = isAll ? allPacksQuery : collectionsQuery;
  const { loading, error } = slug ? collectionQuery : listQuery;
  const productCount = isAll
    ? allPacksQuery.data?.allNonWineSpecialPackProductsCount
    : collection?.productCount;
  const hasMore =
    isAll || slug
      ? products.length < (productCount || 0)
      : collections.length < (collectionsQuery.data?.collectionsCount || 0);
  const isEmpty = slug || isAll ? products.length === 0 : collections.length === 0;

  const fetchMore = async () => {
    if (isFetchingMore || !hasMore) return;
    const requestLocation = locationKeyRef.current;
    setIsFetchingMore(true);
    setHasFetchError(false);
    try {
      if (isAll) {
        await allPacksQuery.fetchMore({ variables: { offset: products.length } });
      } else if (slug) {
        await collectionQuery.fetchMore({ variables: { offset: products.length } });
      } else {
        await collectionsQuery.fetchMore({ variables: { offset: collections.length } });
      }
    } catch (fetchError) {
      logError(String(fetchError), { location: "SpecialPackCollections", action: "FETCH_MORE" });
      if (locationKeyRef.current === requestLocation) setHasFetchError(true);
    } finally {
      if (locationKeyRef.current === requestLocation) setIsFetchingMore(false);
    }
  };

  if (!flagsReady && !flagsError) return <StyledLoading />;
  if (!isEnabled) return <Redirect to={URL_PATTERNS.NOT_FOUND} />;
  if (slug && !loading && !error && !collection) return <Redirect to={URL_PATTERNS.NOT_FOUND} />;

  return (
    <>
      <PageMeta title={t("common:pageMetaTitle", { title: pageTitle })} description={pageDescription} />
      <Hero>
        <Container>
          <HeroTitle>{pageTitle}</HeroTitle>
        </Container>
      </Hero>
      <PacksSection>
        <Container>
          {collection && (
            <>
              <h2>{collection.name}</h2>
              {collection.description && <p>{collection.description}</p>}
            </>
          )}
          {!slug && <PageDescription>{pageDescription}</PageDescription>}
          {error ? (
            <p role="alert">{t("common:sorry")}</p>
          ) : (
            <>
              {!loading && isEmpty && <p>{t("specialPacks:sorryNoBoxes")}</p>}
              {isAll || slug ? (
                <ProductGrid>
                  {products.map(
                    (product) =>
                      product && (
                        <SpecialPackProductCard
                          key={product.slug}
                          product={product}
                          onAdd={setAddingProduct}
                        />
                      )
                  )}
                </ProductGrid>
              ) : (
                collections.map(
                  (item) =>
                    item && (
                      <SpecialPackCollectionGallery
                        key={item.slug}
                        collection={item}
                        onAdd={setAddingProduct}
                      />
                    )
                )
              )}
              {(loading || isFetchingMore) && <StyledLoading />}
              {hasFetchError && <p role="alert">{t("common:sorry")}</p>}
              {hasMore && (
                <Button outline full fullMobile onClick={fetchMore} disabled={loading || isFetchingMore}>
                  {t("common:showMore")}
                </Button>
              )}
            </>
          )}
        </Container>
      </PacksSection>
      {addingProduct?.specialPackEditionSlug && (
        <SpecialPackProductAddTray
          editionSlug={addingProduct.specialPackEditionSlug}
          preselectedProductId={addingProduct.id}
          onClose={() => setAddingProduct(null)}
        />
      )}
    </>
  );
};

export default SpecialPackCollections;
